import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { getEmployees, deleteEmployee, importEmployees, exportEmployees } from '../../../models/employeeModel';
import Swal from 'sweetalert2';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

// Debounced Input for Global Filter
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (value !== initialValue) {
        onChange(value);
      }
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, initialValue, onChange, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

function EmployeesList() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const initialPagination = () => {
    const storedPagination = localStorage.getItem('employeesTablePagination');
    if (storedPagination) {
      return JSON.parse(storedPagination);
    }
    return {
      pageIndex: 0,
      pageSize: 10,
    };
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);

  useEffect(() => {
    localStorage.setItem('employeesTablePagination', JSON.stringify(pagination));
  }, [pagination]);

  const columns = useMemo(() => [
    {
      id: 'employee_code',
      accessorFn: row => row.employee_code,
      header: 'Employee Code',
      cell: ({ row }) => {
        const code = row.original.employee_code;
        return (
          <span className={code ? 'font-medium text-gray-800' : 'text-gray-400 italic'}>
            {code || 'N/A'}
          </span>
        );
      },
    },
    {
      id: 'name',
      accessorFn: row => `${row.first_name} ${row.last_name}`,
      header: 'Name',
      cell: ({ row }) => {
        const emp = row.original;
        return (
          <div>
            <span className="font-medium text-gray-800">
              {emp.first_name} {emp.last_name}
            </span>
          </div>
        );
      },
    },
    {
      id: 'job_title',
      accessorFn: row => row.job_title,
      header: 'Job Title',
      cell: ({ row }) => (
        <span className="text-gray-700">{row.original.job_title || '—'}</span>
      ),
    },
    {
      id: 'paypoint',
      accessorFn: row => row.paypoint,
      header: 'Paypoint',
      cell: ({ row }) => (
        <span className="text-gray-700">{row.original.paypoint || '—'}</span>
      ),
    },
    {
      id: 'contact_number',
      accessorFn: row => row.contact_number,
      header: 'Contact Number',
      cell: ({ row }) => {
        const contact = row.original.contact_number;
        return (
          <span className={contact ? 'text-gray-700' : 'text-gray-400 italic'}>
            {contact || 'N/A'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center space-x-1">
          <button
            type="button"
            className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
            onClick={() => handleDelete(row.original.employee_id, `${row.original.first_name} ${row.original.last_name}`)}
          >
            Delete
          </button>
        </div>
      ),
      enableSorting: false,
    },
  ], []);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.pageIndex + 1);
      params.append('pageSize', pagination.pageSize);
      if (globalFilter) {
        params.append('filter', globalFilter);
      }
      if (sorting.length > 0) {
        params.append('sortBy', sorting[0].id);
        params.append('sortOrder', sorting[0].desc ? 'DESC' : 'ASC');
      }

      const response = await getEmployees(params);
      if (response.status === 1 && response.data && response.data.rows && response.data.meta) {
        setEmployees(response.data.rows);
        setRowCount(response.data.meta.totalItems);
      } else {
        toast.error(response.message || 'Failed to load employees');
        setEmployees([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(error.message || 'An error occurred while loading employees');
      setEmployees([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, globalFilter, sorting]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const table = useReactTable({
    data: employees,
    columns,
    state: {
      pagination,
      globalFilter,
      sorting,
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    debugTable: false,
  });

  const handleDelete = (id, name) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete the employee "${name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'var(--color-accent-danger)',
      cancelButtonColor: 'var(--color-neutral-500)',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteEmployee(id);
          if (response.status === 1) {
            toast.success(`Employee "${name}" deleted successfully!`);
            fetchEmployees();
          } else {
            toast.error(response.message || 'Failed to delete employee');
          }
        } catch (error) {
          console.error('Error deleting employee:', error);
          toast.error(error.response?.data?.message || 'An error occurred while deleting the employee');
        }
      }
    });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const response = await importEmployees(file);
      if (response.status === 1) {
        setImportResult(response.data);
        toast.success(response.message || 'Import completed successfully');
        fetchEmployees();
      } else {
        toast.error(response.message || 'Import failed');
        if (response.data?.errors) {
          setImportResult(response.data);
        }
      }
    } catch (error) {
      console.error('Error importing employees:', error);
      toast.error(error.response?.data?.message || 'An error occurred during import');
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExport = async () => {
    try {
      await exportEmployees();
      toast.success('Export started');
    } catch (error) {
      console.error('Error exporting employees:', error);
      toast.error('Failed to export employees');
    }
  };

  if (loading && employees.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-4">
          <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
            <div className="flex justify-center items-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading employees...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5"
                style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Employees</h1>
                <p className="text-xs text-gray-500 mb-1">Manage employee records imported from HR system</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {rowCount} Total Employees
                  </span>
                  {pagination.pageIndex > 0 && (
                    <span className="text-xs text-gray-400 ml-2">
                      • Page {pagination.pageIndex + 1}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Controls and Actions */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button
                  onClick={() => fetchEmployees()}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>

                {/* Search Input */}
                <DebouncedInput
                  value={globalFilter ?? ''}
                  onChange={value => {
                    setGlobalFilter(String(value));
                    setPagination(p => ({ ...p, pageIndex: 0 }));
                  }}
                  className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search employees..."
                />

                {/* Export Button */}
                <button
                  onClick={handleExport}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  title="Export to CSV"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export
                </button>

                {/* Import Button */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".csv"
                  className="hidden"
                />
                <button
                  onClick={handleImportClick}
                  disabled={importing}
                  className="inline-flex items-center px-3 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200 disabled:opacity-50"
                  style={{
                    backgroundColor: '#0087D4',
                    boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)'
                  }}
                >
                  {importing ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Importing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Import CSV
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Import Result Summary */}
          {importResult && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Import Results</h4>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {importResult.imported} Imported
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                  {importResult.updated} Updated
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {importResult.skipped} Skipped
                </span>
              </div>
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-red-600 mb-1">Errors:</p>
                  <ul className="text-xs text-red-500 list-disc list-inside max-h-24 overflow-y-auto">
                    {importResult.errors.slice(0, 10).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                    {importResult.errors.length > 10 && (
                      <li className="text-gray-500">...and {importResult.errors.length - 10} more errors</li>
                    )}
                  </ul>
                </div>
              )}
              {importResult.warnings && importResult.warnings.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-orange-600 mb-1">Warnings (Employee Code sanitized):</p>
                  <ul className="text-xs text-orange-500 list-disc list-inside max-h-24 overflow-y-auto">
                    {importResult.warnings.slice(0, 10).map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                    {importResult.warnings.length > 10 && (
                      <li className="text-gray-500">...and {importResult.warnings.length - 10} more warnings</li>
                    )}
                  </ul>
                </div>
              )}
              <button
                onClick={() => setImportResult(null)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* CSV Format Info */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-xs text-blue-700">
              <strong>CSV Format:</strong> Employee Code, First Name, Last Name, Job Title, PAYPOINT, CONTACT NUMBER. 
              Employees without an employee code or contact number will still be imported.
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        scope="col"
                        className={`px-3 py-2 text-left text-xs font-semibold uppercase ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                          }`}
                        style={{ color: '#0087D4' }}
                        onClick={header.column.getToggleSortingHandler()}
                        title={
                          header.column.getCanSort()
                            ? header.column.getNextSortingOrder() === 'asc'
                              ? 'Sort ascending'
                              : header.column.getNextSortingOrder() === 'desc'
                                ? 'Sort descending'
                                : 'Clear sort'
                            : undefined
                        }
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <span className="ml-1 text-blue-600">↑</span>,
                          desc: <span className="ml-1 text-blue-600">↓</span>,
                        }[header.column.getIsSorted()] ?? null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && employees.length > 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && table.getRowModel().rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-6 text-center">
                      <p className="text-gray-600">
                        {globalFilter ? "No employees found matching your search." : "No employees found. Import employees using a CSV file."}
                      </p>
                    </td>
                  </tr>
                )}
                {!loading && table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="hover:bg-blue-50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-3 py-2 text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rowCount > 0 && (
            <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-4">
                {/* Results info */}
                <div className="text-gray-500">
                  Showing {(pagination.pageIndex * pagination.pageSize) + 1} to {Math.min((pagination.pageIndex + 1) * pagination.pageSize, rowCount)} of {rowCount} results
                </div>

                {/* Page Size Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Show:</span>
                  <select
                    value={pagination.pageSize}
                    onChange={(e) => {
                      table.setPageSize(Number(e.target.value));
                    }}
                    className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[10, 20, 30, 40, 50].map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  <span className="text-gray-500">per page</span>
                </div>
              </div>

              <div className="flex items-center space-x-1">
                {/* Navigation buttons */}
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
                >
                  First
                </button>

                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
                >
                  Previous
                </button>

                {/* Current page indicator */}
                <div className="flex items-center space-x-1">
                  <span className={`px-3 py-1.5 border rounded text-xs font-medium bg-blue-600 text-white border-blue-600`}>
                    {pagination.pageIndex + 1}
                  </span>
                </div>

                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
                >
                  Next
                </button>

                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmployeesList;
